import { Document, Packer, Paragraph, ImageRun, AlignmentType, BorderStyle, UnderlineType } from "docx";
import { saveAs } from 'file-saver';
import domtoimage from 'dom-to-image';
import html2canvas from 'html2canvas';


const useGeneral = () => {
  const convertDateForIos = (date) => {
    const arr = date.split(/[- : T .]/);
    date = new Date(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5]);
    return date;
  };
  // convert date in whatever format that you want
  const formatDate = (date, format = "Y-mm-dd", convert = true) => {
    if (convert) date = new Date(convertDateForIos(date));

    const map = {
      dd: date.getDate().toString().padStart(2, "0"),
      mm: date.getMonth() + 1 > 9 ? date.getMonth() + 1 : "0" + (date.getMonth() + 1),
      Y: date.getFullYear(),
      yy: date.getFullYear().toString().slice(-2),
      HH: date.getHours() > 9 ? date.getHours() : "0" + date.getHours(),
      MM: date.getMinutes() > 9 ? date.getMinutes() : "0" + date.getMinutes(),
      SS: date.getSeconds() > 9 ? date.getSeconds() : "0" + date.getSeconds(),
    };

    return format.replace(/dd|mm|yy|Y|HH|MM|SS/gi, (matched) => map[matched]);
  };

  // convert date to this string format (yyyy-mm-dd hh:mm:ss) to send to the back
  const dateFormatUTC = (FieldsDate) => {
    const date = formatDate(new Date(FieldsDate[0]), "Y-mm-dd", false);
    const time = new Date(FieldsDate[1]);
    const hour =
      FieldsDate[2] === "pm" && time.getHours() < 12
        ? time.getHours() + 12
        : time.getHours() > 12 && FieldsDate[2] === "am"
          ? time.getHours() - 12
          : time.getHours();

    return `${date} ${hour.toString().padStart(2, "0")}:${time
      .getMinutes()
      .toString()
      .padStart(2, "0")}:00`;
  };

  const getAmPmFormat = (date) => {
    const time = new Date(date);
    const hour = time.getHours();

    return hour >= 12 ? "pm" : "am";
  };

  const openApp = () => {
    window.location = "membeer://";
  };

  const printReportPDF = async (container, selectTool) => {
    return new Promise((resolve, reject) => {
      try {
        // Find the iframe within the container
        const iframe = container.querySelector('iframe');
        const filename = selectTool?.name ? `${selectTool.name}-copilot-analytics.pdf` : 'documento.pdf';

        if (!iframe) {
          reject(new Error('No iframe found in container'));
          return;
        }

        // Set the document title to the desired filename (helps with PDF save name)
        const originalTitle = document.title;
        document.title = filename.replace('.pdf', '');

        // Focus the iframe window
        iframe.contentWindow.focus();

        // Set up print event handlers
        const afterPrintHandler = () => {
          window.removeEventListener('afterprint', afterPrintHandler);
          // Restore original title
          document.title = originalTitle;
          resolve();
        };

        const beforePrintHandler = () => {
          window.removeEventListener('beforeprint', beforePrintHandler);
        };

        window.addEventListener('afterprint', afterPrintHandler);
        window.addEventListener('beforeprint', beforePrintHandler);

        // Trigger print dialog in the iframe
        iframe.contentWindow.print();

      } catch (error) {
        reject(error);
      }
    });
  }

  const generateWord = async (htmlContent, selectTool) => {
    const children = [];

    // Función para procesar encabezados (h1, h2, h3)
    const processHeader = (element, headingLevel) => {
      const cleanText = element.innerText.trim().replace(/\s+/g, ' ');
      children.push(new Paragraph({ text: cleanText, style: headingLevel }));
    };

    // Función para procesar párrafos y listas
    const processParagraphOrList = (element, isList, afterGraphic) => {
      const cleanText = element.innerText.trim().replace(/\s+/g, ' ');
      const text = isList ? `   • ${cleanText}` : cleanText;
      children.push(new Paragraph({ text, style: afterGraphic ? 'aside' : 'wellSpaced' }));
    };

    // Función para procesar gráficos
    const processGraphic = async (element) => {
      try {
        const imageBlob = await domtoimage.toBlob(element, {
          style: true, // Asegura que los estilos sean aplicados
          externalCss: true, // Asegura que los CSS externos sean cargados (si es necesario)
        });
        if (imageBlob) {
          const base64data = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result.split(',')[1]); // Extraer solo el base64
            reader.onerror = () => reject('Error al convertir la imagen a base64');
            reader.readAsDataURL(imageBlob);
          });

          const newWidth = 400;
          const aspectRatio = element.offsetWidth / element.offsetHeight;
          const newHeight = newWidth / aspectRatio;

          const pageWidth = 595;  // Tamaño estándar de página en puntos (A4)
          const pageHeight = 842; // Tamaño estándar de página en puntos (A4)

          // Limitar el tamaño de la imagen si excede el tamaño de la página
          const maxWidth = pageWidth - 72;  // Margen de 1 pulgada (72 puntos)
          const maxHeight = pageHeight - 72;

          const adjustedWidth = Math.min(newWidth, maxWidth);
          const adjustedHeight = Math.min(newHeight, maxHeight);

          children.push(
            new Paragraph({ text: '', style: 'wellSpaced' }),
            new Paragraph({
              children: [
                new ImageRun({
                  data: base64data,
                  transformation: { width: adjustedWidth, height: adjustedHeight }
                })
              ],
              alignment: AlignmentType.CENTER
            }),
            new Paragraph({ text: '', style: 'wellSpaced' })
          );
        }
      } catch (error) {
        console.error('Error al procesar gráfico:', error);
        throw error;
      }
    };

    const processTable = async (element) => {
      try {
        // Paso 1: Capturar la tabla como una imagen usando html2canvas
        const canvas = await html2canvas(element, {
          useCORS: true, // Habilitar CORS si es necesario
          logging: false, // Desactivar logs para mayor rendimiento
        });

        // Paso 2: Convertir el canvas a base64
        const base64Image = canvas.toDataURL('image/png');

        // Paso 3: Calcular las dimensiones ajustadas
        const originalWidth = canvas.width;
        const originalHeight = canvas.height;
        const maxWidth = 595;  // Tamaño estándar de página en puntos (A4)
        const maxHeight = 842; // Tamaño estándar de página en puntos (A4)

        // Calcular la proporción para ajustar el tamaño de la imagen
        const aspectRatio = originalWidth / originalHeight;
        let adjustedWidth = maxWidth - 72; // Margen de 1 pulgada (72 puntos)
        let adjustedHeight = adjustedWidth / aspectRatio;

        // Asegurarse de que la altura no exceda el tamaño máximo
        if (adjustedHeight > maxHeight - 72) {
          adjustedHeight = maxHeight - 72;
          adjustedWidth = adjustedHeight * aspectRatio;
        }

        // Paso 4: Crear el párrafo que contendrá la imagen
        children.push(
          new Paragraph({ text: '', style: 'wellSpaced' }),
          new Paragraph({
            children: [
              new ImageRun({
                data: base64Image.split(',')[1], // Solo la parte base64 de la imagen
                transformation: { width: adjustedWidth, height: adjustedHeight },
              }),
            ],
            alignment: AlignmentType.CENTER,
          }),
          new Paragraph({ text: '', style: 'wellSpaced' })
        );
      } catch (error) {
        console.log('Error al procesar la tabla:', error);
        throw error;
      }
    };

    // Función para procesar secciones
    const processSection = async (section) => {
      const isHeader = section.tagName.toLowerCase() === 'header';
      let afterGraphic = false;
      const elements = section.querySelectorAll('*');

      for (const element of elements) {
        const tag = element.tagName.toLowerCase();
        switch (tag) {
          case 'h1':
            processHeader(element, 'Heading1');
            break;
          case 'h2':
            processHeader(element, 'Heading2');
            break;
          case 'h3':
            processHeader(element, 'Heading3');
            break;
          case 'p':
            processParagraphOrList(element, false, afterGraphic);
            afterGraphic = false;
            break;
          case 'li':
            processParagraphOrList(element, true);
            break;
          case 'div':
            if (element.id.includes('grafico')) {
              await processGraphic(element);
              afterGraphic = true;
            }
            if (element.id.includes('demographic-table')) {
              await processTable(element);
            }
            break;
          default:
            break;
        }
      }

      if (isHeader) {
        children.push(new Paragraph({
          text: '',
          border: {
            bottom: { style: BorderStyle.SINGLE, size: 2, color: '000000' }
          }
        }));
      }
    };

    // Procesar todas las secciones y encabezados
    const iframe = htmlContent.querySelector('iframe');
    const iframeDoc = iframe.contentDocument;
    const sections = iframeDoc.querySelectorAll('section, header');
    for (const section of sections) {
      await processSection(section);
    }

    // Generar el documento Word
    const docStyles = {
      styles: {
        paragraphStyles: [
          {
            id: "Heading1",
            name: "Heading 1",
            basedOn: "Normal",
            next: "Normal",
            quickFormat: true,
            run: {
              size: 28,
              bold: true,
            },
            paragraph: {
              spacing: {
                after: 120,
              },
            },
          },
          {
            id: "Heading2",
            name: "Heading 2",
            basedOn: "Normal",
            next: "Normal",
            quickFormat: true,
            run: {
              size: 26,
              bold: true,
              underline: {
                type: UnderlineType.SINGLE,
              },
            },
            paragraph: {
              spacing: {
                before: 240,
                after: 120,
              },
            },
          },
          {
            id: "aside",
            name: "Aside",
            basedOn: "Normal",
            next: "Normal",
            run: {
              color: "999999",
              italics: true,
            },
            paragraph: {
              indent: {
                left: 720,
              },
              spacing: {
                line: 276,
                before: 72,
                after: 240,
              },
            },
          },
          {
            id: "wellSpaced",
            name: "Well Spaced",
            basedOn: "Normal",
            quickFormat: true,
            paragraph: {
              spacing: { line: 276, before: 72, after: 36 },
            },
          },
        ],
      },
    };
    const doc = new Document({
      ...docStyles,
      sections: [{ children }]
    });

    const fileName = selectTool?.name ? `${selectTool.name}-copilot-analytics.docx` : 'documento.docx';

    try {
      const blob = await Packer.toBlob(doc);
      saveAs(blob, fileName);
    } catch (error) {
      throw error;
    }
  };

  return { dateFormatUTC, formatDate, getAmPmFormat, openApp, generateWord, printReportPDF };
};

export default useGeneral;

